"use client";

import { Button } from "@reecost/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@reecost/ui/dialog";
import * as React from "react";

import { BusinessTypeSelector } from "./business-type-selector";
import type { AuthIntent, BusinessType } from "./types";

const STEP_LABEL: Record<AuthIntent, string> = {
  login: "Login",
  signup: "Sign Up",
};

const AUTH_REDIRECTS: Record<
  AuthIntent,
  Partial<Record<BusinessType, string>>
> = {
  login: {
    retail: "https://retail.reecost.com/login",
    restaurant: "https://pos.reecost.com/",
  },
  signup: {
    retail: "https://retail.reecost.com/register",
    restaurant: "/p-form?product=restaurant",
  },
};

export const AuthModal = ({
  open,
  onOpenChange,
  intent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent: AuthIntent;
}) => {
  const [selectedType, setSelectedType] = React.useState<BusinessType | null>(
    null,
  );
  const [redirectError, setRedirectError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSelectedType(null);
      setRedirectError(null);
    }
  }, [open]);

  const handleSelectType = (businessType: BusinessType) => {
    setSelectedType(businessType);
    const destination = AUTH_REDIRECTS[intent][businessType];
    if (!destination) {
      setRedirectError(
        `No ${STEP_LABEL[intent]} URL configured for this platform yet.`,
      );
      return;
    }

    onOpenChange(false);
    window.location.assign(destination);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="xl"
        overlayClassName="bg-[#12033a]/70 backdrop-blur-sm"
        className="!top-auto !bottom-2 !left-1/2 !w-[calc(100%-1rem)] !max-h-[85dvh] !-translate-x-1/2 !overflow-y-auto !rounded-[20px] !border-[#e7defb] !p-5 sm:!top-1/2 sm:!bottom-auto sm:!w-full sm:!max-w-[560px] sm:!max-h-[90dvh] sm:!translate-y-[-50%] sm:!p-7 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <DialogTitle className="pr-10 text-center font-semibold text-[#320481] text-xl sm:text-2xl">
          Choose Your Platform
        </DialogTitle>
        <DialogDescription className="mt-1 text-center text-[#5b5b6b] text-sm">
          {`Select your platform to continue to ${STEP_LABEL[intent]}.`}
        </DialogDescription>

        <div className="mt-5 space-y-3">
          <BusinessTypeSelector
            value={selectedType}
            onSelect={handleSelectType}
            intent={intent}
          />
          {redirectError ? (
            <p className="text-center text-red-600 text-sm">{redirectError}</p>
          ) : null}
          <div className="flex justify-center pt-1">
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-9 rounded-lg text-[#5b5b6b] hover:bg-[#f4efff] hover:text-[#320481]"
              >
                Cancel
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
